import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { debounce } from 'lodash'
import { isCancel } from 'axios'
import type { PageLayoutStore } from 'shared/layout'
import { TableStore } from 'shared/ui/Table'
import type { ICardFilter } from 'shared/ui'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import type { ITrigger } from 'entities/Trigger/model/Trigger'
import { TriggerApi } from 'entities/Trigger/api/trigger'
import {
  ContactStatistic,
  type IParamsGetContactsStatistic,
  type IResponseContactStatistic,
} from 'entities/ContactStatistic'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'

export class TriggerDetailStore {
  private _contactStatisticsTableStore = new TableStore<ContactStatistic>({
    element: 'contact',
    withoutDefaultManageColumns: true,
  })
  private readonly _trigger: ITrigger
  private readonly _pageLayoutStore: PageLayoutStore

  private _isLoading = false
  private _scrollToTopTrigger = ''

  private _page = 1
  private _limit = 10
  private _total = 0
  private _search = ''

  private _filter: IParamsGetContactsStatistic['filter'] = 'sent_to'

  private _contactStatisticsMap: Map<number, ContactStatistic> = new Map<number, ContactStatistic>()
  private _contactsDetailsManageStore: ContactsDetailsManageStore

  private _disposeChangeParams: IReactionDisposer | null = null
  private _disposeFetchTriggerContacts: IReactionDisposer | null = null
  private _cancelRequest = new RequestCancelAxios()
  private readonly _contactsTagsModalStore: ContactsTagsModalStore
  private _previousSearchValue = ''

  constructor(
    trigger: ITrigger,
    pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)
    this._trigger = trigger
    this._pageLayoutStore = pageLayoutStore
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)

    void this.fetchTriggerContacts()
    void this._contactsDetailsManageStore.init()

    this.reactionChangeParams()
    this.reactionFetchTriggerContacts()
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  reset = () => {
    this._contactsDetailsManageStore.reset()
    this.clearReactions()
  }

  private reactionChangeParams = () => {
    this._disposeChangeParams?.()
    this._disposeChangeParams = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._isLoading = true
        this._page = 1
      }
    )
  }

  private reactionFetchTriggerContacts = () => {
    this._disposeFetchTriggerContacts?.()
    this._disposeFetchTriggerContacts = reaction(
      () => this.params,
      this._fetchTriggerContactsWithDebounce
    )
  }

  private clearReactions = () => {
    this._disposeChangeParams?.()
    this._disposeFetchTriggerContacts?.()
  }

  private setData = (data: IResponseContactStatistic[], total: number) => {
    this._contactStatisticsMap.clear()
    data.forEach((contactStatisticsResponse) => {
      this._contactStatisticsMap.set(
        contactStatisticsResponse.id,
        new ContactStatistic(contactStatisticsResponse)
      )
    })

    this._total = total
  }

  private _debouncedTrackSearchActivity = debounce((searchQuery: string) => {
    this._entities.organizationStore.handleTrackOrganizationActivity({
      event: 'phi.contact.searched',
      data: {
        search_query: searchQuery,
      },
    })
  }, 2000)

  private _fetchTriggerContactsWithDebounce = () => {
    this._cancelRequest.init()

    runInAction(() => {
      this._isLoading = true
    })

    this._debounceFetchTriggerContacts()
  }

  private _debounceFetchTriggerContacts = debounce(() => {
    this.fetchTriggerContacts()
  }, 500)

  private fetchTriggerContacts = async () => {
    let isCancelled = false

    try {
      this._cancelRequest.init()

      runInAction(() => {
        this._isLoading = true
      })

      const {
        data: { data, total },
      } = await TriggerApi.getTriggerContactsStatistic(this._trigger.id, this.params, {
        cancelToken: this._cancelRequest.token,
      })

      if (
        this.params.search !== this._previousSearchValue &&
        this._entities.organizationStore.isHipaaCompliant
      ) {
        this._previousSearchValue = this.params.search
        this._debouncedTrackSearchActivity(this.params.search)
      }

      this.setData(data, total)

      runInAction(() => {
        this._scrollToTopTrigger = nanoid()
      })
    } catch (e) {
      if (isCancel(e)) {
        isCancelled = true
      }
      logger.error(e)
    } finally {
      if (!isCancelled) {
        runInAction(() => {
          this._isLoading = false
        })
      }
    }
  }

  setIsLoading = (isLoading: boolean) => {
    this._isLoading = isLoading
  }

  setSearch = (value: string) => {
    this._search = value
  }

  onActiveFilter = (filter: ICardFilter<IParamsGetContactsStatistic['filter']>) => {
    this._filter = filter.key
  }

  onPaginationContactsTableChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
            inboxId: this._trigger.inboxId,
          },
          'trigger',
          true
        )
      },
    }
  }

  onSuccessDelete = () => {
    this._contactStatisticsTableStore.unselectAllIds()
    void this.fetchTriggerContacts()
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get filters() {
    if (!this.statistics) return []
    const filters: ICardFilter<IParamsGetContactsStatistic['filter']>[] =
      this.statistics.getFilters({ entities: this._entities })

    const relevantColumnsIds =
      this._trigger.type === 'sms' || this._trigger.type === 'rcs'
        ? [
            'sent_to',
            'quiet_hours',
            'success',
            'failed',
            'opted_out',
            'revenue',
            'conversions',
            'clicked',
            'replied',
          ]
        : ['sent_to', 'quiet_hours', 'success', 'failed', 'opted_out', 'replied']

    return filters.filter((column) => relevantColumnsIds.includes(column.key))
  }

  get paramsWithoutPage(): Omit<IParamsGetContactsStatistic, 'page'> {
    return {
      search: removeSpacesFromNumericString(this._search),
      length: this._limit,
      filter: this._filter,
      sortBy: this._contactStatisticsTableStore.sortBy || 'created_at',
      sortOrder: this._contactStatisticsTableStore.sortOrder,
    }
  }

  get params(): IParamsGetContactsStatistic {
    return {
      page: this._page,
      ...this.paramsWithoutPage,
    }
  }

  get statistics() {
    return this._trigger.statistics
  }

  get isNoData() {
    return !this._isLoading && !this._contactStatisticsMap.size && !this._search
  }

  get contactStatisticsItems(): ContactStatistic[] {
    return Array.from(this._contactStatisticsMap.values())
  }

  get filter() {
    return this._filter
  }

  get search() {
    return this._search
  }

  get total() {
    return this._total
  }

  get isLoading() {
    return this._isLoading
  }

  get page() {
    return this._page
  }

  get scrollToTopTrigger() {
    return this._scrollToTopTrigger
  }

  get limit() {
    return this._limit
  }

  get contactStatisticsTableStore() {
    return this._contactStatisticsTableStore
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get trigger() {
    return this._trigger
  }
}
